import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { AsH1, AsLabel, AsSpan, HeadingXS, LabelLG, ParagraphSM } from '@/atoms/Text'
import { options } from '@/services/ContentfulService'
import { isGuildSignupVideoLayout } from '@/services/GuildSignupPageService/validations'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

export const MarketingWinbackVideoHero: React.FC = () => {
  const { layout, sale } = useGuildSignupContext()
  const { t } = useTranslate('guild')

  if (!isGuildSignupVideoLayout(layout)) {
    return null
  }

  const videoLayout = layout

  return (
    <section
      aria-label={videoLayout?.heading ?? undefined}
      className="2xl:max-h-auto relative h-full min-h-screen w-full overflow-hidden"
    >
      <>
        <div className="aspect-h-1 aspect-w-1 relative flex w-full bg-cover md:hidden">
          <video
            className="bg-cover object-cover md:h-full md:max-h-[800px] md:min-h-full"
            autoPlay
            playsInline
            muted
            loop
            src={layout.backgroundVideoUrl}
          />
          <div className="absolute inset-0 h-full w-full bg-gradient-to-b from-transparent to-core-gray-950" />
        </div>
        <div className="relative hidden w-full bg-cover md:aspect-h-9 md:aspect-w-16 md:flex md:min-h-[800px]">
          <video
            className="bg-cover object-cover md:h-full md:max-h-[800px] md:min-h-full"
            autoPlay
            playsInline
            muted
            loop
            src={layout.backgroundVideoUrl}
          />
          <div className="absolute inset-0 h-full w-full bg-gradient-to-b from-transparent to-core-gray-950" />
        </div>
      </>
      <div
        className={classNames(
          'h-screen min-h-screen absolute left-0 top-0 z-10 w-full bg-transparent px-4 md:max-h-[800px] md:px-8 xl:mx-[40px] xl:px-4',
          { 'mt-16 md:mt-0': !!sale },
        )}
      >
        <div className="relative flex h-full w-full flex-col-reverse items-center justify-start lg:!flex-row 2xl:mx-auto 2xl:max-w-[1600px]">
          <div className="-mt-auto flex h-full w-full flex-col items-start justify-center gap-y-4 bg-transparent py-0 opacity-100 md:items-center md:gap-y-6 lg:-mt-40 lg:w-1/2 lg:items-start lg:py-32 2xl:pl-10">
            <div>
              <div className="flex flex-col items-center gap-y-1">
                <GuildHighResShieldIcon className="invisible h-0 w-0 md:visible md:h-full md:w-fit" size={64} />
                <GuildHighResShieldIcon
                  className="invisible h-0 w-0 sm:visible sm:h-full sm:w-fit md:invisible md:h-0 md:w-0"
                  size={48}
                />
                <ParagraphSM color="white" className="hidden uppercase md:flex">
                  {t('guild', 'Guild')}
                </ParagraphSM>
              </div>
            </div>
            <div className="z-20 -mt-[80px] flex flex-col gap-y-4 md:mt-0 md:items-center lg:max-w-[600px] lg:items-start se:max-w-full ">
              <HeadingXS
                as={AsH1}
                weight="bold"
                className="se:photon-title-lg md:photon-heading-lg 2xl:photon-heading-xl w-full text-balance bg-transparent text-center md:text-center lg:text-left"
                color="white"
              >
                {videoLayout.heading}
              </HeadingXS>
              <ParagraphSM
                as={AsSpan}
                className="[&>p]:photon-paragraph-sm md:[&>p]:photon-paragraph-md se:[&>p]:photon-caption-sm 2xl:[&>p]:photon-paragraph-xl mb-3 w-full text-balance bg-transparent text-center md:text-center lg:text-left"
                color="core-gray-400"
              >
                {documentToReactComponents(videoLayout?.paragraph?.json, options)}
              </ParagraphSM>
            </div>
            <div className="flex w-full flex-col items-center !opacity-100 lg:max-w-[600px] lg:items-start">
              <LinkButton
                href="#plan-selection"
                variant="oxide"
                className={'btn my-0 mb-4 min-h-[40px] min-w-[142px] rounded-lg py-2 lg:min-h-[56px] lg:w-2/5 lg:px-0'}
              >
                <LabelLG
                  as={AsLabel}
                  color="white"
                  weight="semibold"
                  className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                >
                  <Translate i18nKey="getStarted" t={t}>
                    Get Started
                  </Translate>
                </LabelLG>
              </LinkButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
